import * as React from 'react'

import { Box, Button } from '@factorialco/deprecated-design-system'
import { CostCentersListDocument } from 'generated/resources/graphql'

import InformationTooltip from 'modules/core/components/Inputs/InformationTooltip'
import Modal from 'modules/core/components/Modal'
import SelectField from 'modules/core/components/SelectField'
import TextField from 'modules/core/components/TextField'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { CreateCostCenterDocument } from 'modules/finance/hooks/useCostCenters'
import { trackCostCenterAction } from 'modules/finance/lib/costCenterHelpers'

interface NewCostCenterModalProps {
  onClose: () => void
}

interface CreateCostCenterFormValues {
  name: string
  code?: string
  description?: string
  legalEntityId: string
}

const NewCostCenterModal = ({ onClose }: NewCostCenterModalProps) => {
  const closeOnSuccess = React.useRef<boolean>(true)
  const [submitting, setSubmitting] = React.useState<boolean>(false)

  const { company } = useCurrentSession()

  const [createFinanceCostCenter] = useMutation(CreateCostCenterDocument, {
    extractorFn: (data) => data.financeMutations.createFinanceCostCenter,
  })

  const form = useForm<CreateCostCenterFormValues>({
    defaultValues: {
      name: '',
      code: '',
      description: '',
      legalEntityId: '',
    },

    validate: (data) => {
      const errors: FormErrors<CreateCostCenterFormValues> = {}
      if (!data.name) {
        errors.name = i18n.t('finance.modals.add_cost_center.mandatory')
      }
      return errors
    },

    onSubmit: async (values) => {
      setSubmitting(true)
      const variables = {
        name: values.name,
        code: values.code?.trim() || null,
        description: values.description?.trim() || null,
        legalEntityId: values.legalEntityId ? Number(values.legalEntityId) : null,
        companyId: company.id,
      }

      const result = await createFinanceCostCenter({
        variables,
        refetchQueries: [CostCentersListDocument],
      })

      const handled = handleResult(result, (errors) => {
        form.setErrors({
          name: errors['name'],
          code: errors['code'],
          description: errors['description'],
        })
        app.setFlash({ type: 'wrong', message: i18n.t('finance.modals.add_cost_center.error') })
        const name = form.values.name
        const code = form.values.code
        const costCenter = {
          name: name,
          code: code,
          activeEmployeesCount: 0,
          historicalEmployeesCount: 0,
        }
        trackCostCenterAction(trackingEvents.accounting.createCostCenterSubmit, costCenter, 'error')
      })

      if (handled.success) {
        if (closeOnSuccess.current) {
          onClose()
        } else {
          form.resetForm()
          closeOnSuccess.current = true
        }
        app.setFlash({ type: 'correct', message: i18n.t('finance.modals.add_cost_center.success') })

        if (handled.data.costCenter) {
          trackCostCenterAction(
            trackingEvents.accounting.createCostCenterSubmit,
            handled.data.costCenter,
            'success'
          )
        }
      }
      setSubmitting(false)
      return handled
    },
  })

  const handleClose = (afterSubmit = false) => {
    if (!afterSubmit) {
      trackCostCenterAction(trackingEvents.accounting.createCostCenterCancel)
    }
    onClose()
  }

  const legalEntityList = company.legalEntities.map((le) => {
    return {
      label: le.legalName || '',
      value: le.id,
    }
  })

  return (
    <Modal
      onClose={handleClose}
      illustration="list-document"
      onSubmit={() => form.handleSubmit()}
      title={i18n.t('finance.modals.add_cost_center.title')}
      description={i18n.t('finance.modals.add_cost_center.description')}
      footer={
        <>
          <Button type="secondary" onClick={onClose} disabled={submitting}>
            {i18n.t('common.cancel')}
          </Button>
          <Button.Split
            submit
            label={i18n.t('finance.cost_centers_list.create.save_and_close')}
            disabled={submitting || !form.values.name.length}
          >
            <Button.Split.Option
              iconName="add"
              onClick={() => {
                closeOnSuccess.current = false
                form.handleSubmit()
              }}
              disabled={submitting || !form.values.name.length}
            >
              {i18n.t('finance.cost_centers_list.create.save_and_create')}
            </Button.Split.Option>
          </Button.Split>
        </>
      }
    >
      <Box paddingBottom="s24">
        <TextField
          mandatory
          label={i18n.t('finance.modals.add_cost_center.fields.name.label')}
          onChange={(value) => form.setValues({ ...form.values, name: value })}
          value={form.values.name}
          error={form.errors.name}
        />
      </Box>
      <Box paddingBottom="s24">
        <TextField
          label={i18n.t('finance.modals.add_cost_center.fields.code.label')}
          value={form.values.code}
          onChange={(value) => form.setValues({ ...form.values, code: value })}
          error={form.errors.code}
          secondaryLabel={
            <InformationTooltip
              information={i18n.t('finance.modals.add_cost_center.fields.code.information')}
              icon="question"
            />
          }
        />
      </Box>
      <Box paddingBottom="s24">
        <SelectField
          label={i18n.t('finance.modals.add_cost_center.fields.legal_entity.label')}
          value={form.values.legalEntityId}
          options={legalEntityList}
          onChange={(value) => form.setValues({ ...form.values, legalEntityId: value })}
          defaultOption={{
            label: i18n.t('finance.modals.add_account.fields.legal_entity.options.all'),
            value: false,
          }}
        />
      </Box>
      <Box paddingBottom="s24">
        <TextField
          label={i18n.t('finance.modals.add_cost_center.fields.description.label')}
          value={form.values.description}
          onChange={(value) => form.setValues({ ...form.values, description: value })}
          error={form.errors.description}
        />
      </Box>
    </Modal>
  )
}

export default NewCostCenterModal
